import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog } from "@/components/ui/dialog";
import { DataTable, type Column } from "@/components/ui/data-table";
import { useAors, useCreateAor, useUpdateAor, useDeleteAor } from "@/hooks/use-aors";
import type { Aor } from "@/types";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

const defaultAor = {
  name: "",
  max_contacts: 1,
  minimum_expiration: 60,
  maximum_expiration: 7200,
  qualify_frequency: 0,
  qualify_timeout: 3.0,
  qualify_2xx_only: false,
  authenticate_qualify: false,
  remove_existing: false,
  remove_unavailable: false,
  support_path: false,
  contact: [] as string[],
  outbound_proxy: null as string | null,
  mailboxes: null as string | null,
};

const columns: Column<Aor>[] = [
  { key: "name", header: "Name" },
  { key: "max_contacts", header: "Max Contacts" },
  {
    key: "contact",
    header: "Contact",
    render: (a) => (
      <span className="text-xs text-muted-foreground">
        {a.contact?.length ? a.contact.join(", ") : "—"}
      </span>
    ),
  },
  { key: "maximum_expiration", header: "Max Expiry" },
  { key: "qualify_frequency", header: "Qualify Freq" },
];

export default function AorsPage() {
  const { data, isLoading } = useAors();
  const createMut = useCreateAor();
  const updateMut = useUpdateAor();
  const deleteMut = useDeleteAor();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Aor | null>(null);
  const [form, setForm] = useState(defaultAor);

  const openCreate = () => { setEditing(null); setForm(defaultAor); setOpen(true); };
  const openEdit = (a: Aor) => {
    setEditing(a);
    const { id, created_at, updated_at, ...rest } = a;
    setForm({ ...rest, contact: rest.contact ?? [] });
    setOpen(true);
  };

  const handleSave = () => {
    if (editing) {
      updateMut.mutate({ id: editing.id, data: form as Partial<typeof form> }, {
        onSuccess: () => { setOpen(false); toast.success("AOR updated"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    } else {
      createMut.mutate(form as typeof form, {
        onSuccess: () => { setOpen(false); toast.success("AOR created"); },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      });
    }
  };

  const handleDelete = (a: Aor) => {
    deleteMut.mutate(a.id, {
      onSuccess: () => toast.success("AOR deleted"),
      onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
    });
  };

  const updateField = (field: string, value: unknown) => setForm((prev) => ({ ...prev, [field]: value }));

  const addContact = () => setForm((prev) => ({ ...prev, contact: [...prev.contact, ""] }));
  const removeContact = (i: number) => {
    setForm((prev) => ({ ...prev, contact: prev.contact.filter((_, idx) => idx !== i) }));
  };
  const updateContact = (i: number, val: string) => {
    setForm((prev) => {
      const next = [...prev.contact];
      next[i] = val;
      return { ...prev, contact: next };
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">AORs</h1>
          <p className="text-sm text-muted-foreground mt-1">Address of Record configurations</p>
        </div>
        <Button onClick={openCreate} size="sm"><Plus size={14} /> Create AOR</Button>
      </div>

      <DataTable columns={columns} data={data} isLoading={isLoading} getRowKey={(a) => a.id} onEdit={openEdit} onDelete={handleDelete} emptyMessage="No AORs configured." />

      <Dialog open={open} onClose={() => setOpen(false)} title={editing ? "Edit AOR" : "Create AOR"} className="max-w-lg">
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name">Name * <HelpIcon text="AOR identifier. Referenced by endpoints. Example: trunk-aor, phone-1001-aor." /></Label>
            <Input id="name" value={form.name} onChange={(e) => updateField("name", e.target.value)} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Contact SIP URIs <HelpIcon text="Where to send calls. Each entry is a sip:user@host:port URI. Multiple contacts = parallel forking (ring all at once). Example: sip:provider.com:5060. Empty = use endpoint name for URI." /></Label>
              <Button type="button" variant="ghost" size="sm" onClick={addContact}>
                <Plus size={12} /> Add
              </Button>
            </div>
            {form.contact.length === 0 && (
              <p className="text-xs text-muted-foreground">No contacts. Click Add.</p>
            )}
            <div className="space-y-1.5">
              {form.contact.map((c, i) => (
                <div key={i} className="flex items-center gap-1">
                  <Input
                    value={c}
                    onChange={(e) => updateContact(i, e.target.value)}
                    placeholder={`sip:${form.name || "endpoint"}@host`}
                    className="flex-1"
                  />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(i)}>
                    <X size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="max_contacts">Max Contacts <HelpIcon text="Maximum simultaneous registrations/bindings. 0 = unlimited. Set to 1 to only allow one device registered at a time." /></Label>
              <Input type="number" id="max_contacts" value={form.max_contacts} onChange={(e) => updateField("max_contacts", Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="minimum_expiration">Min Expiration <HelpIcon text="Minimum registration expiry in seconds. Clients requesting shorter expiry get this minimum. Default 60." /></Label>
              <Input type="number" id="minimum_expiration" value={form.minimum_expiration} onChange={(e) => updateField("minimum_expiration", Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="maximum_expiration">Max Expiration <HelpIcon text="Maximum registration expiry in seconds. Clients requesting longer expiry get capped to this. Default 7200 (2 hours)." /></Label>
              <Input type="number" id="maximum_expiration" value={form.maximum_expiration} onChange={(e) => updateField("maximum_expiration", Number(e.target.value))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="qualify_frequency">Qualify Frequency <HelpIcon text="How often (seconds) to send OPTIONS ping to check endpoint reachability. 0 = disabled. 60 = every 60 seconds. Enables failover detection." /></Label>
              <Input type="number" id="qualify_frequency" value={form.qualify_frequency} onChange={(e) => updateField("qualify_frequency", Number(e.target.value))} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qualify_timeout">Qualify Timeout <HelpIcon text="Seconds to wait for OPTIONS response before marking unreachable. Default 3.0. Shorter = faster detection but more false positives on high-latency links." /></Label>
              <Input type="number" step="0.1" id="qualify_timeout" value={form.qualify_timeout} onChange={(e) => updateField("qualify_timeout", Number(e.target.value))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="outbound_proxy">Outbound Proxy <HelpIcon text="SIP URI of an outbound proxy for calls to this AOR. All requests go through this proxy. Example: sip:proxy.provider.com:5060." /></Label>
              <Input id="outbound_proxy" value={form.outbound_proxy ?? ""} onChange={(e) => updateField("outbound_proxy", e.target.value || null)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mailboxes">Mailboxes <HelpIcon text="Mailbox(es) for MWI (Message Waiting Indicator). Format: mailbox@context. Example: 1001@default." /></Label>
              <Input id="mailboxes" value={form.mailboxes ?? ""} onChange={(e) => updateField("mailboxes", e.target.value || null)} />
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mt-2">
            <Switch id="qualify_2xx_only" checked={form.qualify_2xx_only} onChange={(e) => updateField("qualify_2xx_only", e.target.checked)} label={<span>Qualify 2xx Only <HelpIcon text="Only treat 2xx (success) OPTIONS responses as reachable. Non-2xx responses count as unreachable." /></span>} />
            <Switch id="authenticate_qualify" checked={form.authenticate_qualify} onChange={(e) => updateField("authenticate_qualify", e.target.checked)} label={<span>Auth Qualify <HelpIcon text="Send OPTIONS pings with authentication credentials. Required if endpoint requires auth for OPTIONS." /></span>} />
            <Switch id="remove_existing" checked={form.remove_existing} onChange={(e) => updateField("remove_existing", e.target.checked)} label={<span>Remove Existing <HelpIcon text="When a new registration arrives, remove all previous contacts first. Useful for limiting to one device." /></span>} />
            <Switch id="remove_unavailable" checked={form.remove_unavailable} onChange={(e) => updateField("remove_unavailable", e.target.checked)} label={<span>Remove Unavailable <HelpIcon text="Automatically remove contacts that fail qualify checks. Helps keep the AOR clean." /></span>} />
            <Switch id="support_path" checked={form.support_path} onChange={(e) => updateField("support_path", e.target.checked)} label={<span>Support Path <HelpIcon text="Enable Path header support (RFC 3327). Required when endpoint is behind a SIP proxy." /></span>} />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={createMut.isPending || updateMut.isPending}>{editing ? "Update" : "Create"}</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}